import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { ProgramCard, ProgramDetails, ProgramIntro, ProgramRow } from "@/components/services/ProgramCard";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { ArrowUpRight, Check, X } from "lucide-react";
import { SITE } from "@/content/site";
import { SERVICES } from "@/content/services";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { RelatedReading } from "@/components/RelatedReading";
import { faqSchema } from "@/lib/schema";

const SERVICE = SERVICES.find((s) => s.slug === "digital-marketing")!;

export const Route = createFileRoute("/services/digital-marketing")({
  head: () => buildSeo({
    title: "Digital Marketing Expert in Bangladesh — SEO-led Growth Strategy | Freelancer Tamal",
    description: "Digital marketing consultant in Bangladesh. Full-funnel strategy combining SEO, AEO, content, email and social, with a 90-day plan and KPIs agreed in writing.",
    path: "/services/digital-marketing",
    ogEyebrow: "Digital Marketing",
    keywords: "best digital marketing expert Bangladesh, digital marketing consultant Rangpur, freelance digital marketer Bangladesh, digital marketing strategy, SEO-led growth, organic marketing consultant",
  }),
  component: DigitalMarketingPage,
});

function DigitalMarketingPage() {
  const s = SERVICE;
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "Digital Marketing", path: "/services/digital-marketing" }]} />
      <Section headingLevel={1}
        eyebrow="Digital Marketing"
        title={<>Digital marketing expert in <span className="text-gradient">Bangladesh.</span></>}
        subtitle="An organic-first growth strategy that ships — SEO, AEO, content, email, and social, working as one system instead of competing for budget."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "I plan and run full-funnel digital marketing for founders, agencies and ecommerce brands — anchored in SEO and AEO so growth compounds instead of renting.",
              "Every engagement starts with a 90-day execution roadmap tied to 2–3 north-star KPIs (pipeline, organic revenue, branded search) — not vanity metrics.",
              "Paid is planned and advised; organic is executed hands-on. For Meta/Google paid execution I bring trusted specialist partners.",
            ]}
          />
          <ReviewedBy reviewedOn="2026-06-01" credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh" />
        </div>
      </Section>

      <ProgramCard>
        <ProgramIntro label="Service" title={s.title} tagline={s.tagline}>
          <p className="mt-4 text-muted-foreground">{s.description}</p>
          <div className="mt-8">
            <Link to="/book" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
              Book a strategy call <ArrowUpRight size={16} />
            </Link>
          </div>
        </ProgramIntro>
        <ProgramDetails deliverables={s.deliverables} outcomes={s.outcomes}>
          <ProgramRow heading="People also ask">
            <FaqAccordion items={s.faqs} idPrefix="faq-digital-marketing" withSchema={false} />
          </ProgramRow>
        </ProgramDetails>
      </ProgramCard>

      <Section eyebrow="Me vs typical agencies" title={<>How my engagement <span className="text-gradient">compares.</span></>} subtitle="A side-by-side look at how the work is scoped, measured and reviewed — so you know what you're buying before the kickoff call.">
        <Reveal>
          <div className="rounded-3xl border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-secondary/40">
                  <tr className="text-left">
                    <th className="px-6 py-5 font-display text-base w-1/3">Dimension</th>
                    <th className="px-6 py-5 font-display text-base">
                      <span className="text-gradient">Working with me</span>
                    </th>
                    <th className="px-6 py-5 font-display text-base text-muted-foreground">Typical agency</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    {
                      label: "Who you actually work with",
                      mine: "Senior consultant — me, hands-on, every week.",
                      agency: "Sales lead, then juniors execute. Strategy lives in slides.",
                    },
                    {
                      label: "Time to first shipped work",
                      mine: "Audit + 90-day roadmap in week 1. Execution starts week 2.",
                      agency: "Often a longer onboarding and discovery phase before the first changes ship.",
                    },
                    {
                      label: "Channels covered",
                      mine: "SEO, AEO, GEO, content, email, organic social, CRO — as one integrated system.",
                      agency: "Single-channel pods (just SEO, or just paid) that don't talk to each other.",
                    },
                    {
                      label: "Deliverables per month",
                      mine: "Agreed in writing before work starts: content pieces, technical fixes, AEO/GEO assets and a monthly KPI report.",
                      agency: "Varies widely — ask any provider to list monthly deliverables in writing.",
                    },
                    {
                      label: "Reporting & KPIs",
                      mine: "2–3 north-star KPIs (pipeline, organic revenue, branded search). Live dashboard + monthly call.",
                      agency: "Sometimes centred on impressions, keyword counts or DR rather than leads and revenue.",
                    },
                    {
                      label: "AI search readiness (AEO/GEO)",
                      mine: "Built in from day one — entity SEO, schema, llms.txt, ChatGPT/Perplexity citation tracking.",
                      agency: "Check whether it is included or sold as a separate service.",
                    },
                    {
                      label: "What to expect by month 3",
                      mine: "Priority technical fixes shipped, key pages improved, and a baseline for rankings and AI citations recorded.",
                      agency: "Depends on the provider — ask for a dated plan for the first 90 days.",
                    },
                    {
                      label: "How progress is judged at month 6",
                      mine: "Search Console clicks, rankings, leads and AI citations compared against the month-1 baseline — no fixed result is promised.",
                      agency: "Ask how results are measured before you sign.",
                    },
                    {
                      label: "Review at month 12",
                      mine: "A plain review of cost against measured leads and revenue, and a decision to continue, change scope or stop.",
                      agency: "Long lock-ins can make it hard to stop if results are unclear.",
                    },
                    {
                      label: "Pricing model",
                      mine: "Written quote after a scoping call. See the pricing page for current starting prices.",
                      agency: "Pricing and contract length vary; check for setup fees and minimum terms.",
                    },
                  ].map((row) => (
                    <tr key={row.label} className="align-top">
                      <td className="px-6 py-5 font-medium">{row.label}</td>
                      <td className="px-6 py-5">
                        <div className="flex items-start gap-2">
                          <Check size={16} className="text-primary mt-1 flex-none" />
                          <span>{row.mine}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-muted-foreground">
                        <div className="flex items-start gap-2">
                          <X size={16} className="mt-1 flex-none opacity-60" />
                          <span>{row.agency}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-4 text-xs text-muted-foreground max-w-2xl">
            The agency column describes common patterns to check for, not any specific company. Results depend on your starting point, competition and how much work ships; no ranking or traffic outcome is guaranteed.
          </p>
        </Reveal>
      </Section>

      <Section title={<>Looking for a specific channel? <span className="text-gradient">Start there.</span></>}>
        <div className="flex flex-wrap gap-3">
          <Link to="/services" hash="seo" className="rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary">SEO</Link>
          <Link to="/services/aeo" className="rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary">AEO</Link>
          <Link to="/services/geo" className="rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary">GEO</Link>
          <Link to="/services" hash="content-strategy" className="rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary">Content</Link>
          <Link to="/services" hash="local-seo" className="rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary">Local SEO</Link>
          <Link to="/bn/blog" className="rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary">বাংলায় পড়ুন →</Link>
        </div>
      </Section>

      <RelatedReading
        eyebrow="Deep-dive guides"
        title={<>Strategy reading for <span className="text-gradient">marketing leaders.</span></>}
        hrefs={[
          "/blog/digital-marketing-2025",
          "/blog/state-of-seo-bangladesh-2026",
          "/blog/what-is-aeo-how-to-get-cited-by-chatgpt-2026",
          "/blog/keyword-research-that-brings-traffic",
          "/blog/best-seo-tools-2025",
          "/blog/why-89-percent-websites-fail-at-seo",
        ]}
      />

      <RelatedReading
        eyebrow="Pricing & ROI"
        title={<>Plan the <span className="text-gradient">budget.</span></>}
        description="Benchmarks for what marketing and SEO retainers cost in Bangladesh, plus a 90-day roadmap you can budget against."
        hrefs={[
          "/blog/seo-service-price-in-bangladesh-2026",
          "/blog/90-day-seo-roadmap-bangladesh-small-business-2026",
          "/blog/monthly-seo-package-bangladesh-2026",
        ]}
      />

      <JsonLd data={{
        "@context": "https://schema.org", "@type": "Service",
        name: "Digital Marketing Strategy",
        serviceType: "Digital Marketing Consulting",
        description: s.description,
        areaServed: ["Bangladesh", "Worldwide"],
        provider: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName, url: `${SITE.url}/about`, jobTitle: "Digital Marketing Expert", sameAs: Object.values(SITE.social) },
      }} />
      <JsonLd data={faqSchema(s.faqs)} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "Digital Marketing", path: "/services/digital-marketing" }])} />
    </>
  );
}